/* Files printed PDFs straight into the shop's own Windows folders.
   The owner picks one folder once per computer (say
   Documents\Dhunna Jewellers Dandenong) and from then on every PDF lands in the
   right subfolder inside it — Tax Invoice, Loan Agreement, Repairs and so on.
   Nothing outside that one folder is ever visible to the app. */

const DB = "dhunna-filing";
const STORE = "handles";
const KEY = "pdf-root";

export const filingSupported = () =>
  typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const idb = async (mode, run) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = run(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
};

const readHandle = () => idb("readonly", (s) => s.get(KEY)).catch(() => undefined);
const writeHandle = (h) => idb("readwrite", (s) => s.put(h, KEY));
const clearHandle = () => idb("readwrite", (s) => s.delete(KEY));

/** The chosen folder's name, or "" when this computer has not been set up. */
export const filingFolderName = async () => (await readHandle())?.name || "";

export const forgetFilingFolder = async () => {
  await clearHandle().catch(() => {});
};

/** Opens the Windows folder picker. Must be called from a click. */
export const chooseFilingFolder = async () => {
  const handle = await window.showDirectoryPicker({
    id: "dhunna-pdf-root",
    mode: "readwrite",
    startIn: "documents",
  });
  await writeHandle(handle);
  return handle.name;
};

const usableHandle = async () => {
  const handle = await readHandle();
  if (!handle) return null;
  const opts = { mode: "readwrite" };
  if ((await handle.queryPermission(opts)) === "granted") return handle;
  // After a browser restart Windows asks again; we are inside a click, so this is allowed.
  if ((await handle.requestPermission(opts)) === "granted") return handle;
  return null;
};

/**
 * Writes a PDF into <chosen folder>\<subfolder>\<filename>, creating the subfolder
 * the first time it is needed and overwriting a file of the same name.
 * Returns the display path, or null when this computer has no folder set up
 * (the caller then falls back to a normal download).
 */
export const filePdf = async (blob, subfolder, filename) => {
  if (!filingSupported()) return null;
  const root = await usableHandle();
  if (!root) return null;
  const dir = await root.getDirectoryHandle(subfolder, { create: true });
  const file = await dir.getFileHandle(filename, { create: true });
  const writer = await file.createWritable();   // truncates, so same name overwrites
  await writer.write(blob);
  await writer.close();
  return `${root.name}\\${subfolder}\\${filename}`;
};

/**
 * The one way staff get a file out of the portal. It lands in the shop's own folder
 * when this computer has been set up; otherwise it downloads as normal and the toast
 * offers to pick a folder there and then. Never used on customer-facing screens.
 */
export const deliverFile = async (blob, subfolder, filename, toast) => {
  try {
    const path = await filePdf(blob, subfolder, filename);
    if (path) {
      toast.success(`Filed into ${path}`);
      return path;
    }
  } catch (err) {
    toast.error("Could not write to the shop folder — downloading instead");
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  if (!filingSupported()) {
    toast.success(`${filename} downloaded — look in your Downloads folder`);
    return null;
  }
  toast.success(`${filename} downloaded — look in your Downloads folder`, {
    duration: 9000,
    action: {
      label: "File these in a folder instead",
      onClick: async () => {
        try {
          const name = await chooseFilingFolder();
          toast.success(`Set up — files will go into ${name} from now on`);
        } catch (err) {
          if (err?.name !== "AbortError") toast.error("Could not use that folder");
        }
      },
    },
  });
  return null;
};
